import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Eye, Loader2, Plus, Trash2, UserCheck, UserPlus, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { del, errorMessage, get, post, put } from "@/lib/api";
import { EMAIL_RE, ENGINEER_JOB_TYPES, TEAM_ROLE_LABELS, WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { DatePicker } from "@/components/DatePicker";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Section = ({ title, children }) => (
  <section className="space-y-3 border-t border-slate-100 pt-4 first:border-0 first:pt-0">
    <h3 className="font-display text-sm font-bold text-slate-900">{title}</h3>
    {children}
  </section>
);

function DetailsForm({ company, onSaved }) {
  const [name, setName] = useState(company.name);
  const [trial, setTrial] = useState(company.trial_ends_at ? company.trial_ends_at.slice(0, 10) : "");
  const [announcement, setAnnouncement] = useState(company.announcement || "");
  const [enabledTypes, setEnabledTypes] = useState(company.enabled_user_types || WORKER_ROLE_OPTIONS.map(([role]) => role));
  const [engineerJobTypes, setEngineerJobTypes] = useState(company.engineer_job_types ?? ENGINEER_JOB_TYPES);
  const [newJobType, setNewJobType] = useState("");
  const [visitTypes, setVisitTypes] = useState(company.visit_types ?? []);
  const [newVisitType, setNewVisitType] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { toast.error("Enter a company name"); return; }
    if (engineerJobTypes.some((jobType) => !jobType.trim())) { toast.error("Remove empty Job Types before saving"); return; }
    if (visitTypes.length > 20 || visitTypes.some((visitType) => !visitType.trim())) { toast.error("Visit Types must contain 1 to 20 non-empty values"); return; }
    setBusy(true);
    try {
      await put(`/platform/companies/${company.id}`, {
        name: name.trim(), trial_ends_at: trial || null, announcement,
        enabled_user_types: enabledTypes, engineer_job_types: engineerJobTypes, visit_types: visitTypes,
      });
      toast.success("Company updated");
      onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} noValidate className="space-y-4" data-testid="owner-company-details-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label htmlFor="cd-name">Company name</Label><Input id="cd-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="company-edit-name-input" /></div>
        <div>
          <Label htmlFor="cd-trial">Trial end date</Label>
          <DatePicker id="cd-trial" value={trial} onChange={setTrial} className="mt-1.5 w-full" testId="company-edit-trial-input" />
          {company.complimentary && <p className="mt-1 text-xs text-slate-500">Complimentary companies don't need a trial.</p>}
        </div>
      </div>
      <div>
        <Label htmlFor="cd-announcement">Company announcement</Label>
        <Textarea id="cd-announcement" rows={3} maxLength={500} value={announcement} onChange={(e) => setAnnouncement(e.target.value)} className="mt-1.5" data-testid="owner-company-announcement-input" />
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-slate-900">Enabled user types</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {WORKER_ROLE_OPTIONS.filter(([role]) => !(company.sitewide_disabled_types || []).includes(role)).map(([role, label]) => (
            <label key={role} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3">
              <Checkbox checked={enabledTypes.includes(role)} onCheckedChange={(checked) => setEnabledTypes((current) => checked ? [...current, role] : current.filter((value) => value !== role))} data-testid={`owner-company-user-type-${role}`} />
              <span className="text-sm font-medium text-slate-800">{label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {enabledTypes.includes("engineer") && <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-slate-900">Engineer Job Types</legend>
        <div className="flex gap-2">
          <Input value={newJobType} onChange={(e) => setNewJobType(e.target.value)} maxLength={60} placeholder="Add a Job Type" aria-label="New Engineer Job Type" data-testid="owner-company-job-type-input" />
          <Button type="button" size="icon" variant="outline" aria-label="Add Job Type" disabled={!newJobType.trim() || engineerJobTypes.length >= 20} data-testid="owner-company-job-type-add" onClick={() => {
            const next = newJobType.trim();
            if (!next || engineerJobTypes.length >= 20 || engineerJobTypes.some((value) => value.toLowerCase() === next.toLowerCase())) return;
            setEngineerJobTypes((current) => [...current, next]);
            setNewJobType("");
          }}><Plus className="h-4 w-4" /></Button>
        </div>
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {engineerJobTypes.map((jobType) => (
            <li key={jobType} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span>{jobType}</span>
              <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${jobType}`} onClick={() => setEngineerJobTypes((current) => current.filter((value) => value !== jobType))}><X className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      </fieldset>}
      {enabledTypes.includes("agent") && <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-slate-900">Visit Types</legend>
        <div className="flex gap-2">
          <Input value={newVisitType} onChange={(e) => setNewVisitType(e.target.value)} maxLength={60} placeholder="Add a Visit Type" aria-label="New Visit Type" data-testid="owner-company-visit-type-input" />
          <Button type="button" size="icon" variant="outline" aria-label="Add Visit Type" disabled={!newVisitType.trim() || visitTypes.length >= 20} data-testid="owner-company-visit-type-add" onClick={() => {
            const next = newVisitType.trim();
            if (!next || visitTypes.length >= 20 || visitTypes.some((value) => value.toLowerCase() === next.toLowerCase())) return;
            setVisitTypes((current) => [...current, next]);
            setNewVisitType("");
          }}><Plus className="h-4 w-4" /></Button>
        </div>
        {visitTypes.length > 0 ? <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {visitTypes.map((visitType) => (
            <li key={visitType} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span>{visitType}</span>
              <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${visitType}`} onClick={() => setVisitTypes((current) => current.filter((value) => value !== visitType))}><X className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul> : <p className="text-sm text-slate-500">No Visit Types configured.</p>}
      </fieldset>}
      <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="company-edit-save-button">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save details</Button>
    </form>
  );
}

function InviteForm({ companyId, onInvited }) {
  const [f, setF] = useState({ full_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (f.full_name.trim().length < 2 || !EMAIL_RE.test(f.email.trim())) { toast.error("Enter a name and a valid email"); return; }
    setBusy(true);
    try {
      const r = await post(`/platform/companies/${companyId}/locksmiths/invite`, { full_name: f.full_name.trim(), email: f.email.trim(), postcodes: [] });
      setLink(r.invite_link || null);
      toast.success(r.existing_account ? "Invite sent. They'll see it when they sign in." : "Locksmith added. Share the set-password link below.");
      setF({ full_name: "", email: "" });
      onInvited();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <Input placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="company-invite-name-input" />
        <Input type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="company-invite-email-input" />
        <Button type="submit" disabled={busy} variant="outline" data-testid="company-invite-button">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1 h-4 w-4" />} Add</Button>
      </div>
      {link && (
        <div className="flex items-center gap-2 rounded-md bg-blue-50 p-2 text-xs text-blue-900" data-testid="company-invite-link">
          <span className="truncate">{link}</span>
          <Button type="button" size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} data-testid="company-invite-copy"><Copy className="h-3.5 w-3.5" /></Button>
        </div>)}
    </form>
  );
}

const TeamList = ({ team, onRemove, onAccept }) => (
  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="company-team-list">
    {team.length === 0 && <li className="p-3 text-sm text-slate-500">No team members yet.</li>}
    {team.map((m) => (
      <li key={m.id} className="flex items-center justify-between gap-2 p-3" data-testid={`company-member-${m.id}`}>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{m.full_name || m.email}</p>
          <p className="truncate text-xs text-slate-500">{m.email} · {TEAM_ROLE_LABELS[m.role] || m.role}{m.membership_status === "invited" ? " · Awaiting acceptance" : ""}{m.active === false ? " · Inactive" : ""}</p>
        </div>
        {m.membership_status === "invited" && (
          <Button size="sm" variant="outline" className="shrink-0" onClick={() => onAccept(m)} data-testid={`company-member-accept-${m.id}`}><UserCheck className="mr-1 h-4 w-4" /> Accept for them</Button>)}
        {WORKER_ROLE_OPTIONS.some(([role]) => role === m.role) && (
          <Button size="icon" variant="ghost" className="text-rose-700" aria-label="Remove from company" onClick={() => onRemove(m)} data-testid={`company-member-remove-${m.id}`}><Trash2 className="h-4 w-4" /></Button>)}
      </li>))}
  </ul>
);

const ViewAsButton = ({ company }) => {
  const { refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      await post(`/platform/support-view/start/${company.id}`);
      await refreshProfile();
      navigate("/manager");
    } catch (e) {
      toast.error(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="text-sm text-amber-900">See their app exactly as their manager does. You can make changes (except billing). Ends after 1 hour.</p>
      <Button type="button" variant="outline" onClick={start} disabled={busy} data-testid="company-view-as-button">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Eye className="mr-1 h-4 w-4" />}View as manager
      </Button>
    </div>
  );
};

export const CompanyDialog = ({ company, onOpenChange, onSaved }) => {
  const [team, setTeam] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const load = () => get(`/platform/companies/${company.id}/team`).then(setTeam).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => {
    if (company) { setTeam(null); load(); }
  }, [company?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!company) return null;

  const remove = async () => {
    try {
      await del(`/platform/companies/${company.id}/locksmiths/${removing.id}`);
      toast.success(`${removing.full_name || removing.email} removed`);
      load();
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
    return undefined;
  };

  const accept = async () => {
    try {
      await post(`/platform/companies/${company.id}/locksmiths/${accepting.id}/accept`);
      toast.success(`${accepting.full_name || accepting.email} accepted and added to ${company.name}`);
      load();
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
    return undefined;
  };

  return (
    <Dialog open={!!company} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="company-dialog">
        <DialogHeader><DialogTitle className="font-display">{company.name}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <Section title="Support view"><ViewAsButton company={company} /></Section>
          <Section title="Company details"><DetailsForm key={JSON.stringify([company.id, company.name, company.trial_ends_at, company.announcement, company.enabled_user_types, company.engineer_job_types, company.visit_types])} company={company} onSaved={onSaved} /></Section>
          <Section title="Add a locksmith"><InviteForm companyId={company.id} onInvited={() => { load(); onSaved(); }} /></Section>
          <Section title="Team">{team ? <TeamList team={team} onRemove={setRemoving} onAccept={setAccepting} /> : <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}</Section>
        </div>
        <ConfirmDialog open={!!accepting} onOpenChange={(o) => !o && setAccepting(null)} onConfirm={accept} confirmLabel="Accept invite"
          title={`Accept the invite for ${accepting?.full_name || accepting?.email}?`} description={`They'll join ${company.name} straight away and see its jobs next time they sign in.`} />
        <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} onConfirm={remove} confirmLabel="Remove"
          title={`Remove ${removing?.full_name || "this locksmith"}?`} description="They'll lose access to this company's jobs. Their account and any other companies stay as they are." />
      </DialogContent>
    </Dialog>
  );
};
